import type { StorageAdapter } from '@/services/storage/storage-adapter';
import type { Task, CreateTaskInput, UpdateTaskInput, TaskCategory } from '@/types/task';
import { recordPendingDelete } from './sync-service';

export class TaskService {
  constructor(private adapter: StorageAdapter) {}

  async getTasks(seasonId: string): Promise<Task[]> {
    return this.adapter.getTasks(seasonId);
  }

  async getTasksByCategory(seasonId: string, category: TaskCategory): Promise<Task[]> {
    return this.adapter.getTasksByCategory(seasonId, category);
  }

  async getUpcomingTasks(limit?: number): Promise<Task[]> {
    return this.adapter.getUpcomingTasks(limit);
  }

  async getBlockedTasks(seasonId: string): Promise<Task[]> {
    return this.adapter.getBlockedTasks(seasonId);
  }

  async createTask(data: CreateTaskInput): Promise<Task> {
    return this.adapter.createTask(data);
  }

  async updateTask(id: string, data: UpdateTaskInput): Promise<Task> {
    return this.adapter.updateTask(id, data);
  }

  async completeTask(id: string): Promise<Task> {
    return this.adapter.completeTask(id);
  }

  async uncompleteTask(id: string): Promise<Task> {
    return this.adapter.updateTask(id, { completed: false, completedAt: undefined });
  }

  /**
   * Recompute the `blocked` flag for all tasks in a season based on current completion state.
   * Call after uncompleting a task to re-block any dependents that lost their prerequisite.
   */
  async recomputeBlockedState(seasonId: string): Promise<void> {
    const tasks = await this.adapter.getTasks(seasonId);
    const completionMap = new Map<string, boolean>(tasks.map((t) => [t.id, t.completed]));

    const updates: Promise<Task>[] = [];
    for (const task of tasks) {
      if (!task.dependsOn || task.dependsOn.length === 0) continue;
      const shouldBeBlocked = task.dependsOn.some((depId) => !completionMap.get(depId));
      if (task.blocked !== shouldBeBlocked) {
        updates.push(this.adapter.updateTask(task.id, { blocked: shouldBeBlocked }));
      }
    }

    await Promise.all(updates);
  }

  async snoozeTask(id: string, until: Date): Promise<Task> {
    return this.adapter.snoozeTask(id, until);
  }

  async deleteTask(id: string): Promise<void> {
    await this.adapter.deleteTask(id);
    await recordPendingDelete(id, 'task');
  }

  async bulkCreateTasks(data: CreateTaskInput[]): Promise<Task[]> {
    return this.adapter.bulkCreateTasks(data);
  }

  /**
   * Returns non-completed tasks whose dueDate falls within the current calendar month.
   * Bounds: first day of month (inclusive) through last day of month (inclusive).
   */
  async getTasksDueThisMonth(): Promise<Task[]> {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    const firstStr = firstDay.toISOString().slice(0, 10);
    const lastStr = lastDay.toISOString().slice(0, 10);

    const upcoming = await this.adapter.getUpcomingTasks();
    return upcoming.filter((t) => t.dueDate >= firstStr && t.dueDate <= lastStr);
  }
}
